import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import {
  ModalActionButtonStyles,
  ModalBaseStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { toggleResponseState } from "../../Redux/reducers/responseSlice";
import { errorToastMessage } from "../../utils/toast";
import { useParams } from "react-router-dom";
import http from "../../utils/http";

const CreateResponseModal = ({ showModal, closeModal }: any) => {
  const { id: studyId, participantId } = useParams();
  const [submitLoader, setSubmitLoader] = useState(false);

  const dispatch = useAppDispatch();
  const screeningAttemptId = useAppSelector(
    (state) => state.response.screeningAttemptId,
  );
  const selectedForm = useAppSelector((state) => state.response.selectedForm);
  const isRegistryParticipant = useAppSelector(
    (state) => state.response.isRegistryParticipant,
  );

  const [hasCompletedScreening, setHasCompletedScreening] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setIsStatusLoading(true);
        let url;

        if (isRegistryParticipant) {
          url = `/admin/registry/participants/${participantId}/flags`;
        } else {
          url = `/participants/${participantId}/study-flags?studyId=${studyId}`;
        }
        const response = await http.get(url);

        let data = response.data?.data;

        if (isRegistryParticipant) {
          data = data?.registry?.hasCompletedRegistryScreener;
        } else {
          data = data?.hasCompletedScreener;
        }

        setHasCompletedScreening(data);
      } catch (error) {
        errorToastMessage(error as Error);
      } finally {
        setIsStatusLoading(false);
      }
    };
    fetchStatus();
  }, [participantId, isRegistryParticipant, studyId]);

  const submitHandler = async () => {
    try {
      setSubmitLoader(true);
      const formId = selectedForm?.id;
      const url = isRegistryParticipant
        ? `/registry/admin-screening-responses/clone?participantId=${participantId}`
        : `/study/${studyId}/admin-screening-responses/clone?participantId=${participantId}`;
      await http.post(url);
      closeModal();
      dispatch(toggleResponseState());
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={{ ...ModalBaseStyles, minHeight: "20vh" }}>
        <ModalHeader
          title={screeningAttemptId ? "Edit Responses" : "Create Screener"}
          onCloseClick={closeModal}
        />
        {isStatusLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "10vh",
            }}
          >
            <CircularProgress size={30} />
          </Box>
        ) : !hasCompletedScreening ? (
          screeningAttemptId ? (
            <>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                This participant has not completed their screening responses
                yet.
              </Typography>
              <Typography variant="body1" fontWeight="regular">
                Continuing will make their form read only, and a copy of their
                current response will be created to be editable by you. Any new
                active screening forms will also be prepared. Are you sure you
                want to continue?
              </Typography>
            </>
          ) : (
            <Typography variant="body1" fontWeight="regular">
              Do you wish to create the screening responses for this
              participant?
            </Typography>
          )
        ) : (
          <Typography variant="body1" fontWeight="regular">
            Continuing will reopen screening for editing if locked, and prepare
            any new active screening forms. Existing admin answers will not be
            overwritten. Are you sure you want to continue?
          </Typography>
        )}
        <Box sx={{ ...ModalActionButtonStyles, mt: 2 }}>
          {!submitLoader ? (
            <>
              <Button onClick={closeModal} variant="outlined">
                Cancel
              </Button>
              <Button
                disabled={isStatusLoading}
                onClick={submitHandler}
                variant="contained"
              >
                Confirm
              </Button>
            </>
          ) : (
            <CircularProgress size={25} />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateResponseModal;
